import CryptoJS from 'crypto-js';

// Retrieve the encryption key from environment variables.
const SECRET_KEY = process.env.REACT_APP_STORAGE_SECRET || 'xxx';

/**
 * StorageService: A utility object for securely storing, retrieving, and removing values from localStorage with encryption.
 */
const StorageService = {
  /**
   * Stores a value securely in localStorage after encrypting it.
   * @param key - The key under which to store the value.
   * @param value - The value to store.
   * @returns A promise that resolves when the value is stored successfully.
   */
  storeValue: async (key: string, value: string): Promise<void> => {
    if (!key || !value) {
      throw new Error('Key and value are required to store data.');
    }
    try {
      const encryptedValue = CryptoJS.AES.encrypt(value, SECRET_KEY).toString();
      localStorage.setItem(key, encryptedValue);
    } catch (error: any) {
      throw new Error(`Failed to store value: ${error.message}`);
    }
  },

  /**
   * Retrieves and decrypts a stored value from localStorage.
   * @param key - The key under which the value is stored.
   * @returns A promise that resolves with the decrypted value, or null if not found.
   */
  getValue: async (key: string): Promise<string | null> => {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) {
        return null;
      }
      const bytes = CryptoJS.AES.decrypt(encryptedValue, SECRET_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error: any) {
      throw new Error(`Failed to retrieve value: ${error.message}`);
    }
  },

  /**
   * Removes a stored value from localStorage.
   * @param key - The key under which the value is stored.
   * @returns A promise that resolves when the value is removed successfully.
   */
  removeValue: async (key: string): Promise<void> => {
    try {
      localStorage.removeItem(key);
    } catch (error: any) {
      throw new Error(`Failed to remove value: ${error.message}`);
    }
  },
};

export default StorageService;
